import {
  CalendarIcon,
  ChatWithUsIcon,
  CopyIcon,
  CustomerCareIcon,
  FAQIcon,
  MailIcon,
  NavCloseIcon,
  PhoneIcon
} from 'assets/icons';
import { CustomButton, CustomModal, Loader } from 'components';
import { useGetTeamHelpQuery } from 'containers/app/api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useCallback, useEffect } from 'react';
import { VoidFn } from 'types/baseTypes';
import { SUPPORT_MAIL } from 'config';
import { NotifierTypes } from 'constants/notifierConstants';
import { copyToClipBoard, openInNewTab } from 'utils/commonUtils';
import { formatPhoneNumber } from 'utils/phoneNumberUtils';
import { openScanpayFAQInNewTab } from 'utils/urlUtils';
import HelpSupportRow from './HelpSupportRow';
import { ButtonType } from 'components/custom-button/CustomButton';
import { showNotification } from 'utils/Notifier';

const CSM_AVAILABILITY_TEXT = 'Mon-Fri: 9AM - 6PM (EST)';
const PHONE_COPIED_MESSAGE = 'Phone number copied to clipboard.';
const EMAIL_COPIED_MESSAGE = 'Email copied to clipboard.';
const CSM_UNAVAILABLE_DISCLAIMER = 'Unavailable on federal bank holidays.';

interface HelpSupportPopupProps {
  open: boolean;
  onClose: VoidFn;
}

const HelpSupportPopup: FC<HelpSupportPopupProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const teamId = currentTeam?.id ?? null;
  const {
    data: helpData,
    isLoading,
    isError
  } = useGetTeamHelpQuery({ teamId: teamId ?? '' }, { skip: !open || teamId === null });

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, handleEscape]);

  const handleFaqClick = () => {
    openScanpayFAQInNewTab();
  };

  const handleChatClick = () => {
    if (typeof window !== 'undefined' && window.fcWidget) window.fcWidget.open();
    onClose();
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${helpData?.customerCareEmail ?? SUPPORT_MAIL}`;
    onClose();
  };

  const handleCallClick = () => {
    if (helpData?.customerCarePhone) {
      window.location.href = `tel:${helpData.customerCarePhone}`;
      onClose();
    }
  };

  const handleCopyCsmPhone = () => {
    const phone = helpData?.csmDetails?.phone?.trim() || '+917232443584';
    copyToClipBoard(phone.replace(/\s/g, ''));
    showNotification(dispatch, NotifierTypes.SUCCESS, PHONE_COPIED_MESSAGE);
  };

  const handleScheduleMeetingClick = () => {
    if (helpData?.csmDetails?.meetingLink) openInNewTab(helpData.csmDetails.meetingLink);
  };

  const handleCsmPhoneClick = () => {
    if (helpData?.csmDetails?.phone) {
      window.location.href = `tel:${helpData.csmDetails.phone}`;
    }
  };

  if (!open) return null;

  return (
    <CustomModal open={open} width="md:max-w-[480px] md:w-[480px]" className="max-h-[90vh] overflow-y-auto">
      <div className="relative p-6" role="dialog" aria-label="Help and Support">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2.5 top-2.5 cursor-pointer rounded p-1 hover:bg-borderGray"
          aria-label="Close Help and Support">
          <NavCloseIcon />
        </button>

        <h2 className="mb-6 pr-8 text-17px font-semibold text-primaryText">Help & Support</h2>

        {isLoading && (
          <div className="flex min-h-[200px] items-center justify-center py-8">
            <Loader />
          </div>
        )}

        {!isLoading && (
          <div className="flex flex-col gap-6">
            {helpData?.csmDetails?.name?.trim() && (
              <section aria-label="Meet your Customer Success Manager">
                <h3 className="mb-3 text-sbase font-semibold text-primaryText">Meet your Customer Success Manager</h3>
                <div className="rounded-lg bg-gradient-to-r from-secondary to-[#66B0DD] p-4 text-primary">
                  <div className="mb-4 flex flex-row items-start gap-4">
                    {helpData.csmDetails.avatar ? (
                      <img
                        src={helpData.csmDetails.avatar}
                        alt=""
                        className="h-14 w-14 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary">
                        <CustomerCareIcon className="h-7 w-7 text-primary" />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col gap-1.5">
                      <span className="text-base font-semibold text-primary">{helpData.csmDetails.name.trim()}</span>
                      {helpData.csmDetails?.phone?.trim() && (
                        <div className="flex flex-row items-center gap-1">
                          <span
                            className="flex cursor-pointer items-center gap-1.5 text-sbase text-primary"
                            onClick={handleCsmPhoneClick}>
                            <PhoneIcon className="path-fill-current h-4 w-4 shrink-0 text-primary" />
                            {formatPhoneNumber(helpData.csmDetails.phone)}
                          </span>
                          <button
                            type="button"
                            onClick={handleCopyCsmPhone}
                            className="shrink-0 rounded p-0.5 hover:bg-primary/20"
                            aria-label="Copy phone number">
                            <CopyIcon className="path-fill-current h-4 w-4 text-primary" />
                          </button>
                        </div>
                      )}
                      <span className="flex items-center gap-1.5 text-sbase text-primary">
                        <CalendarIcon className="path-fill-current h-4 w-4 shrink-0 text-primary" />
                        {CSM_AVAILABILITY_TEXT}
                      </span>
                      <p className="text-xs text-primary/90">
                        <span className="text-primary">*</span>
                        {CSM_UNAVAILABLE_DISCLAIMER}
                      </p>
                    </div>
                  </div>
                  {helpData.csmDetails?.meetingLink && (
                    <CustomButton
                      id="help-support-csm-schedule"
                      type={ButtonType.SECONDARY}
                      onClick={handleScheduleMeetingClick}
                      className="w-full "
                      StartIcon={CalendarIcon}
                      startIconStyle="h-5 w-5">
                      Schedule a meeting
                    </CustomButton>
                  )}
                </div>
              </section>
            )}

            <section aria-label="How can we help you">
              <h3 className="mb-3 text-sbase font-semibold text-primaryText">How can we help you?</h3>
              <div className="flex flex-col gap-2">
                <HelpSupportRow
                  icon={<FAQIcon className="h-5 w-5" />}
                  title="Frequently Asked Questions"
                  subtitle="Find answers to common questions"
                  onClick={handleFaqClick}
                  ariaLabel="Open Frequently Asked Questions"
                />
              </div>
            </section>

            <section aria-label="Need more help">
              <h3 className="mb-3 text-sbase font-semibold text-primaryText">Need more help?</h3>
              <div className="flex flex-col gap-2">
                {helpData?.customerCareEmail && (
                  <HelpSupportRow
                    icon={<MailIcon className="path-fill-current h-5 w-5 text-accentText" />}
                    title="Email us"
                    subtitle={helpData?.customerCareEmail ?? SUPPORT_MAIL}
                    onClick={handleEmailClick}
                    ariaLabel="Email us"
                    showCopyIcon={true}
                    copyMessage={EMAIL_COPIED_MESSAGE}
                  />
                )}
                <HelpSupportRow
                  icon={<ChatWithUsIcon className="h-5 w-5" />}
                  title="Chat with us"
                  subtitle={CSM_AVAILABILITY_TEXT}
                  onClick={handleChatClick}
                  ariaLabel="Chat with us"
                />
                {helpData?.customerCarePhone && (
                  <HelpSupportRow
                    icon={<PhoneIcon className="h-5 w-5" />}
                    title="Call us"
                    subtitle={formatPhoneNumber(helpData?.customerCarePhone) || ''}
                    onClick={handleCallClick}
                    ariaLabel="Call us"
                    description={CSM_AVAILABILITY_TEXT}
                    showCopyIcon={true}
                    copyMessage={PHONE_COPIED_MESSAGE}
                  />
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default HelpSupportPopup;
